import { config } from '../config/environment';

const ADMIN_API_BASE_URL = config.adminApiBaseUrl;

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

class AdminApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = ADMIN_API_BASE_URL.trim();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl.trim()}${endpoint}`;
    
    console.log('Base URL:', this.baseUrl);
    console.log('Endpoint:', endpoint);
    console.log('Full URL:', url);
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log('Making request to:', url);
      const response = await fetch(url, defaultOptions);
      const data = await response.json();
      
      console.log('Response status:', response.status);
      console.log('Response data:', data);
      
      if (!response.ok) {
        throw new Error(data.error || `API request failed: ${response.status}`);
      }

      // Handle different response formats
      if (data.success !== undefined) {
        // API returns { success: true, data: [...] }
        return data;
      } else if (Array.isArray(data)) {
        // API returns array directly
        return { success: true, data: data as T };
      } else {
        // API returns object directly
        return { success: true, data: data as T };
      }
    } catch (error) {
      console.error('Admin API request error:', error);
      throw error;
    }
  }

  // Users API
  async getUsers(): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/users');
  }

  async getUser(userId: number): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/users/${userId}`);
  }



  async createUser(userData: { email: string }): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(userId: number, userData: { email: string }): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Permissions API
  async getPermissions(): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/permissions');
  }

  async getPermission(permissionId: number): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/permissions/${permissionId}`);
  }

  async createPermission(permissionData: { name: string; description: string }): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/permissions', {
      method: 'POST',
      body: JSON.stringify(permissionData),
    });
  }

  async updatePermission(permissionId: number, permissionData: { name: string; description: string }): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/permissions/${permissionId}`, {
      method: 'PUT',
      body: JSON.stringify(permissionData),
    });
  }

  async deletePermission(permissionId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  // Groups API
  async getGroups(): Promise<ApiResponse<any[]>> {
    return this.makeRequest<any[]>('/groups');
  }

  async getGroup(groupId: number): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/groups/${groupId}`);
  }

  async createGroup(groupData: { name: string }): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/groups', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async updateGroup(groupId: number, groupData: { name: string }): Promise<ApiResponse<any>> {
    return this.makeRequest<any>(`/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });
  }

  async deleteGroup(groupId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  // Group Management API
  async addUserToGroup(groupId: number, userId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/groups/${groupId}/users`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async removeUserFromGroup(groupId: number, userId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/groups/${groupId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async addPermissionToGroup(groupId: number, permissionId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/groups/${groupId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission_id: permissionId }),
    });
  }

  async removePermissionFromGroup(groupId: number, permissionId: number): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/groups/${groupId}/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }
}

export const adminApi = new AdminApiService(); 